"use client";

// Role-based persona switching — v0.2.3, extended with a Brand role v0.2.6,
// wired to the Slack session in the Slack-login phase.
//
// IT IS A LENS, NOT A COSTUME. Switching role changes WHAT YOU SEE — which
// brands, which creators, which nav — and nothing else. It does not change who
// you are. Everything you DO, you do as yourself: a comment written while
// viewing as Acsah is signed by you, and says so, because the person who has to
// answer it needs to know who actually typed it. "Impersonation" was the wrong
// word for this and it had already leaked into the data (comments carried the
// VIEWED-AS role in authorRole, so a comment you wrote was tooltipped "AM:
// Acsah"). The word is gone; so is the behaviour.
//
// TWO personas, and collapsing them is a security bug:
//
//   identityPersona — who you signed in as. Immutable, from the session (or the
//                     dev bypass). Drives isAdmin and canSwitch, signs every
//                     action, and nothing else may.
//   persona         — the LENS you are looking through. Equal to the identity
//                     for everyone but a LENS HOLDER — an admin, or a staff seat
//                     (AM, AM Ops, Creative Specialist) — who may change it. It
//                     scopes data. It must never be written into a record as the
//                     actor.
//
// The rule the scoping helpers below implement, and the whole point of the lens:
//
//   admin   → every brand, every creator
//   am      → the brands in their pod, and the creators assigned to them
//   creator → the brands they are assigned to
//   brand   → the creators assigned to them
//
// The lens is in the URL — `?as=<key>` on every lens holder's address — so Back
// and Forward step through lens switches, and it PERSISTS in a cookie
// (lib/persona-cookie.ts) for every address that names none: it survives a
// reload and carries into a new tab. An admin lives in another role's view for
// hours at a stretch — Admin's own nav is a console, so a work surface is only
// reachable by looking through the lens of the role that owns it — and losing
// that on every refresh made the app unusable for the person who uses it most.
// An AM working as her creator or her brand is in the same position.
//
// The old objection to persisting it stands, and is answered rather than
// ignored: a stored view must never load anyone into a window their identity
// was not offered. So the URL and the cookie only ever SELECT from the roster
// the server built from the identity (activeLensFor, switchablePersonas), and
// are honoured only for a role that may hold one — checked again below, because
// the roster arrives as a prop and a prop is not a proof. A staff seat's roster
// is their own brands' creators, writers and brands, each narrowed to those
// brands, so a lens only ever narrows what the identity could already reach.
// canSwitch comes from the session and never from storage, so the URL and the
// cookie choose a view, never a privilege.
//
// The script-scoping helpers (scopedScriptsFor / visibleCreatorsFor) used to
// live here and be computed by the provider on every render of every page. They
// are in lib/scoped-scripts.ts now: the scripts
// are in the store only on the pages that read them, and the Videos page, their
// one reader, asks scopedScriptsFor itself. So this provider reads no store.

import { createContext, useCallback, useContext, useDeferredValue, useMemo, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

// Lives in lib/persona-types.ts so server-only code (auth.ts, the identity map,
// the proxy) can import it without pulling this "use client" module along.
// Re-exported so every existing consumer keeps importing it from here.
import type { Persona, BrandRef, PodLead, SwitchableLens } from "@/lib/persona-types";
import { podsInView, type ScopePod } from "@/lib/scope-tiers";
// Pure, and therefore server-callable. They live in lib/ and not here precisely
// because app/layout.tsx has to call them — see the header of persona-scope.ts
// for the runtime-only bug that taught us the difference.
import { visibleBrandsFor, brandLabelIn, creatorSeatFor, sitsInAmSeat, personaEquals } from "@/lib/persona-scope";
import type { CreatorSeat } from "@/lib/persona-scope";
import { LENS_PARAM } from "@/lib/lens-url";
import { writeLensCookie } from "@/lib/stale-lens";
export type { Persona, BrandRef, CreatorSeat, SwitchableLens };
export { visibleBrandsFor, brandLabelIn, creatorSeatFor };

/**
 * Where a freshly signed-in identity lands. Admin is a VIEW role — an AM who
 * sees every brand — so an admin lands as themselves. Narrowing them to one
 * AM's roster on login was the opposite of what the role means, and it also
 * hid every admin-gated nav group, since those gate on the ACTIVE persona.
 * Nobody is stranded: the switcher is gated on canSwitch, never persona.role.
 */
export function defaultActiveFor(identity: Persona): Persona {
  return identity;
}

interface PersonaContextValue {
  /** The persona being VIEWED AS. All scoping derives from this. */
  persona: Persona;
  /**
   * Switch the lens to one of `switchablePersonas`, and write its key to the
   * cookie. No-op unless the signed-in identity holds a lens (canSwitch). Takes the LENS, not
   * a bare persona, because the key is on the lens and the cookie holds the key.
   */
  setPersona: (lens: SwitchableLens) => void;
  /**
   * The KEY that names the lens on screen (`admin`, a slug, `brand:<slug>`) —
   * what `?as=` says and the cookie follows. null for everyone who holds no
   * lens (a creator, a writer, a brand user), whose URL carries none.
   */
  lensKey: string | null;
  /**
   * The `?as=` the lens on screen was read from, raw (null when the address
   * names none). Usually the address bar's; for the one render a same-page Back
   * lags behind it, the one the page on screen was drawn for. What
   * components/lens-url-sync.tsx compares `lensKey` against, so it never reads a
   * lens and an address one render apart as a mismatch to correct.
   */
  urlLens: string | null;
  /**
   * True iff the SIGNED-IN identity is an admin. Never derive this from
   * `persona`. Admin POWER (the grade reset), not the lens: an AM holds a lens
   * too, and asks canSwitch for it.
   */
  isAdmin: boolean;
  /**
   * True iff the SIGNED-IN identity may look through another lens: an admin or
   * a staff seat (AM, AM Ops, Creative Specialist) whose server-built roster is
   * not empty. Gates the switcher, the URL's `?as=` and the cookie. Never derive
   * this from `persona`, which is a creator while an AM views as one.
   */
  canSwitch: boolean;
  /** Display name of the signed-in human, e.g. "Jonathan Chew". */
  userName: string;
  /** The signed-in email. The comment authorId — never compare display names. */
  userEmail: string;
  /**
   * Display label for the LENS, e.g. "AM: Acsah". Chrome only — the switcher and
   * the topbar. It is what you are LOOKING AT, so it must never be recorded as
   * who DID something. Use identityLabel for that.
   */
  label: string;
  /**
   * Display label for the signed-in identity, e.g. "Admin (full access)". The
   * role that signs an action. Constant for the session, whatever lens is on.
   */
  identityLabel: string;
  /**
   * Every brand the SIGNED-IN identity may ever see, id and label together.
   *
   * From the server, because it cannot be derived from the scripts: a brand with
   * nothing written for it yet still has to appear in the AM's pickers. An admin
   * gets all of them (they may look through any lens); everyone else gets theirs.
   */
  brands: BrandRef[];
  /** Brand SLUGS the ACTIVE persona may read. */
  visibleBrands: string[];
  /**
   * The ones among `brands` that are ARCHIVED. Still in `brands`, because an
   * archived brand's old rows still need a label; left off the pickers on the
   * scripts pages, where an archived brand is somewhere nobody works any more.
   */
  archivedBrands: ReadonlySet<string>;
  /** A brand slug's display name. The only sanctioned way to render a slug. */
  brandLabel: (slug: string) => string;
  /**
   * The lenses the identity may switch to, each with the KEY that names it
   * (`admin`, a person's slug, `brand:<slug>`), minted by the server's
   * `switchablePersonas(identity)`. The first is always the identity's own
   * (`admin`, or a staff seat's own slug). Empty for everyone who cannot switch
   * — it is a roster, and a creator has no business holding one. Resolve a key
   * against it with lib/persona-cookie's `personaForKey`. A staff seat's creator
   * and writer lenses carry `scopeBrands`: pass them on as they are.
   */
  switchablePersonas: SwitchableLens[];
  /**
   * A person's SLUG — their name in a URL — or null. Only for the people the
   * signed-in identity can see (every login, for an admin); see peopleVisibleTo.
   *
   * ⚠️ A NULL IS NOT A LICENCE TO PUT THE EMAIL IN THE URL. Somebody with no
   * login has no slug, and a page decides what its link does without one.
   */
  slugFor: (email: string) => string | null;
  /**
   * The address a slug names, or null — the inverse of slugFor, over the same
   * people. Exact match, so an email-shaped param comes back null and the caller
   * decides whether to try it as an address (`emailForSlug(p) ?? p`).
   */
  emailForSlug: (slug: string) => string | null;
  /** Videos-only aliases also cover creators who have no login. */
  videoSlugFor: (email: string) => string | null;
  videoEmailForSlug: (slug: string) => string | null;
  /**
   * The pods the LENS can see, each with its brands — the scope bar's pod tier
   * (lib/scope-tiers' podsInView). Empty for a lens outside the AM's seat; an
   * identity outside it is handed no pods by the server in the first place
   * (lib/server/scope-pods.ts). A pod name never reaches a creator or a brand (P3).
   */
  pods: ScopePod[];
}

const PersonaContext = createContext<PersonaContextValue | null>(null);

/** One empty map for every render that is handed none, so the memo below holds. */
const NO_SLUGS: Record<string, string> = {};
const NO_BRAND_PODS: Record<string, string> = {};
const NO_POD_LEADS: PodLead[] = [];
const NO_ARCHIVED: string[] = [];
const NO_LENSES: SwitchableLens[] = [];

/** The lens a render falls back to when the URL names none: a key (if it has one) and its persona. */
interface HeldLens {
  key: string | null;
  persona: Persona;
}

/**
 * The chrome label. Reads the persona's DISPLAY name, never its key — the one
 * place a display name is the right thing to reach for, because a label is all
 * this produces.
 */
function labelFor(p: Persona, brands: BrandRef[] = []): string {
  if (p.role === "admin") return "Admin (full access)";
  if (p.role === "am") return `AM: ${p.displayName}`;
  if (p.role === "am_ops") return `AM Ops: ${p.displayName}`;
  if (p.role === "creative_specialist") return `Creative Specialist: ${p.displayName}`;
  // EVERY BRAND THEY ARE LOOKING AS, by label, alphabetical: one for an admin's
  // brand lens, and for most brand users; two or more for one who manages
  // several. It names the view, not the person, whose name is in the account menu.
  if (p.role === "brand")
    return `Brand: ${p.brands
      .map((slug) => brandLabelIn(brands, slug))
      .sort((a, b) => a.localeCompare(b))
      .join(", ")}`;
  // NAMES BOTH PEOPLE, and has to. A writer's whole context is "whose scripts am
  // I in" — they may be the only person on this page who is not the person the
  // week belongs to, and every other surface (the week heading, a minted draft's
  // byline) says the creator's name. A bare "Writer: Priya" beside "Jay · Week 4"
  // leaves the connection to be guessed.
  if (p.role === "writer") return `Writer: ${p.displayName} (for ${p.creatorName})`;
  // NAMED IN FULL, and before the bare-creator fallback below, which would
  // otherwise label them "Creator: …" — the one label in this list that would
  // be actively wrong. A Creator Expert casts creators; they are not one, and
  // they cannot see a single script.
  if (p.role === "creator_expert") return `Creator Expert: ${p.displayName}`;
  return `Creator: ${p.displayName}`;
}



// ─── The Scripts-page powers ────────────────────────────────────────────────
//
// ⚠️ EVERY GATE LEFT IN THIS FILE IS A SCRIPTS-PAGE GATE, and that is the whole
// of what a role-permission function is allowed to be now. WHICH PAGES a role
// may open is one list, in lib/nav-config.ts, asked through canOpen — never a
// predicate like these. The two used to be mixed: `canEditSchedule` was both
// "may you edit a post date on the Scripts page" and, because the Schedule page
// gated on it, "may you open the Schedule page". They are not the same
// question, and answering them with one function is how an admin ended up able
// to run a page that appears on no admin's sidebar.
//
// AM OPS, A CREATIVE SPECIALIST AND ADMIN CARRY AN AM'S KEYS on every gate
// below — the same week and post-date powers, over the brands they can see. See stage-model's `acting`,
// which is the same decision one layer down.

/**
 * Who may edit a batch's POST DATE, the pencil on the Scripts page.
 *
 * NOT "who may open the Schedule page", which is what this function used to be
 * doing second and which now lives in NAV_BY_ROLE (am and am_ops; an admin
 * views as the AM who owns the brand). An admin keeps this one, because on the
 * Scripts page an admin IS an AM and this is a Scripts-page control.
 *
 * Read off the ACTIVE persona, so an admin impersonating a creator loses it.
 */
export function canEditPostDates(p: Persona): boolean {
  return sitsInAmSeat(p.role);
}

/**
 * Who may delete a whole week — the only control in the app that takes work
 * away (lib/week-delete.ts). THE AM'S, like the post dates the week lives in:
 * whoever runs a brand's weeks may end one that should never
 * have existed. AM Ops and admin hold an AM's keys here
 * as everywhere on this page.
 *
 * It used to need an admin IDENTITY as well, on the argument that a pod of AMs
 * each able to delete a creator's week is a way for work to go missing. That
 * made it the one gate on the Scripts page reading two personas, and the one
 * thing an admin could do there that an AM could not. Both are gone: the lens
 * alone decides, and an admin on this page is simply an AM.
 */
export function canDeleteWeek(p: Persona): boolean {
  return sitsInAmSeat(p.role);
}

export function PersonaProvider({
  children,
  identityPersona,
  initialPersona,
  initialLensKey,
  userName,
  userEmail,
  brands = [],
  switchablePersonas = [],
  slugByEmail = NO_SLUGS,
  creatorTokens = NO_SLUGS,
  brandPods = NO_BRAND_PODS,
  podLeads = NO_POD_LEADS,
  archivedBrands = NO_ARCHIVED,
}: {
  children: React.ReactNode;
  identityPersona: Persona;
  /** The lens this render was drawn through (URL, then cookie), already resolved against the roster by the server. */
  initialPersona?: Persona | null;
  /** That lens's key (activeLensFor's `key`). Looked up among `switchablePersonas` when absent. */
  initialLensKey?: string | null;
  userName: string;
  userEmail: string;
  /** Brands the identity may see, from the server. Slugs need labels; only the server has them. */
  brands?: BrandRef[];
  /** A lens holder's roster, and empty otherwise — the server decides, not this component. */
  switchablePersonas?: SwitchableLens[];
  /** email → slug, for the people this identity can see. From the server (app/layout.tsx). */
  slugByEmail?: Record<string, string>;
  /** Scoped aliases for creators with work but no login; retained after a login is added. */
  creatorTokens?: Record<string, string>;
  /** brand slug → its pod's value, for the identity's brands. From the server, and only for the AM's seat. */
  brandPods?: Record<string, string>;
  /** Those pods' pills — a lead's slug and label, never their address. */
  podLeads?: PodLead[];
  /** Slugs among `brands` that are archived. From the server. */
  archivedBrands?: string[];
}) {
  const isAdmin = identityPersona.role === "admin";
  // THE ROLES THAT MAY HOLD A LENS are exactly the AM's seat (admin, AM, AM Ops,
  // Creative Specialist); the roster itself is the server's. Both, because the
  // roster is a prop: a creator handed one by mistake still switches nothing.
  // An admin holds one whatever the roster, as before staff seats had one; a
  // staff seat with an empty roster (no slug of their own) holds none.
  const canSwitch = isAdmin || (sitsInAmSeat(identityPersona.role) && switchablePersonas.length > 0);

  // ⚠️ THE LENS IS THE URL'S, READ ON EVERY RENDER. `?as=<key>` names it,
  // resolved against the roster the server handed over — keys only, so a URL can
  // select one of the offered lenses and never describe one. Nothing but the
  // address can move it, so a navigation to a new `?as=` changes the lens in the
  // SAME commit as the server render it fetched: the switcher's label, the
  // sidebar and every client page move together with the server-rendered data
  // around them, which is the property the topbar used to buy with a transition
  // over a cookie write and a refresh. And Back is just another navigation: the
  // entry you return to names its own lens.
  //
  // WHAT IS HELD IS THE LAST LENS A URL NAMED, for an address that names none —
  // a server page's own href, a typed address — which the server renders from
  // the cookie. It starts as the server's answer for this document (the
  // URL, then the cookie) and follows every lens a URL names after that; the
  // cookie follows the same lens (components/lens-url-sync.tsx), so a page the
  // server renders from the cookie and this agree. The address is then stamped
  // with the cookie's key, so the held lens is only ever read on its own
  // for a render or two.
  //
  // Seeded synchronously, so the first render is already the right view — there
  // is no effect-tick during which an admin viewing as an AM sees the console.
  // The canSwitch re-check is not redundant with the layout's: these are props,
  // and a prop is only as trustworthy as whoever passed it.
  //
  // ⚠️ THE ROSTER'S OWN OBJECTS, never a persona rebuilt from fields: a staff
  // seat's creator lens carries `scopeBrands`, and a copy that dropped it would
  // widen the lens to every brand that creator works on.
  const roster = canSwitch ? switchablePersonas : NO_LENSES;
  const [held, setHeld] = useState<HeldLens>(() => {
    const persona = canSwitch && initialPersona ? initialPersona : defaultActiveFor(identityPersona);
    const key = !canSwitch
      ? null
      : initialLensKey ?? roster.find((l) => personaEquals(l.persona, persona))?.key ?? null;
    return { key, persona };
  });

  // ⚠️ ON A BACK THAT STAYS ON THE PAGE, THE PAGE LANDS ONE RENDER LATE — SO
  // THE LENS DOES TOO (PR #413; persona-gating.spec's Back-and-Forward test,
  // red twice on CI). React renders a transition started in a `popstate` event
  // synchronously, and Next 16 keeps the same page segment across a change of
  // query alone, whose `useDeferredValue(rsc)` (layout-router.js) then shows the
  // PREVIOUS page for that synchronous render and the restored one in the
  // deferred render after it. Read straight off the URL, the lens moved in the
  // first: "Admin (full access)" over the AM's page for 25–140 ms, 9 of 9
  // traversals measured. So the query is read through the same deferral when
  // the route is unchanged, and lands in the same render as the page. When the
  // route changes the page mounts afresh with no lag, and so does this (the
  // current query, not the deferred one). Outside a popstate a navigation is
  // a transition, where a deferred value IS the latest: nothing moves there.
  // `null` outside Next's router (a unit test that mounts no router).
  const pathname = usePathname();
  const query = useSearchParams();
  const here = useMemo(() => ({ pathname, query }), [pathname, query]);
  const drawn = useDeferredValue(here);
  const asParam = (drawn.pathname === pathname ? drawn.query : query)?.get(LENS_PARAM) ?? null;
  const fromUrl = asParam ? roster.find((l) => l.key === asParam) ?? null : null;
  // React's "information from previous renders": the lens a URL names becomes
  // the one held, in the render that reads it. React re-renders this component
  // at once with the new state and throws the first attempt away, so no frame is
  // ever drawn from the old lens — which is why `held` alone is the lens on
  // screen, and there is no `fromUrl ?? held` (a mutation test found that half
  // dead).
  if (fromUrl && fromUrl.key !== held.key) setHeld(fromUrl);
  const current = held;
  const persona = current.persona;

  // Enforced here rather than by hiding the switcher: someone who holds no lens
  // and reaches setPersona by any route still cannot change what they see — and
  // therefore cannot write the cookie either.
  //
  // It does NOT move the lens while the URL names one: the URL wins, so a switch
  // is this AND a navigation to the new `?as=` (persona-topbar.tsx). What it
  // does is write the cookie at once — the durable statement of what was asked
  // for — and make the new lens the fallback.
  const setPersona = useCallback(
    (lens: SwitchableLens) => {
      if (!canSwitch) return;
      setHeld(lens);
      writeLensCookie(lens.key);
    },
    [canSwitch],
  );

  // Inverted once per map rather than per lookup. The map arrives with the
  // layout, so it changes only when the server hands over a new one.
  const emailBySlug = useMemo(() => {
    const out = new Map<string, string>();
    for (const [email, slug] of Object.entries(slugByEmail)) out.set(slug, email);
    return out;
  }, [slugByEmail]);
  const emailByCreatorToken = useMemo(() => new Map(Object.entries(creatorTokens).map(([email, token]) => [token, email])), [creatorTokens]);

  const visibleBrands = visibleBrandsFor(persona, brands);
  const archived = useMemo(() => new Set(archivedBrands), [archivedBrands]);

  const value: PersonaContextValue = {
    persona,
    lensKey: canSwitch ? current.key : null,
    urlLens: asParam,
    setPersona,
    isAdmin,
    canSwitch,
    userName,
    userEmail,
    identityLabel: labelFor(identityPersona, brands),
    label: labelFor(persona, brands),
    brands,
    visibleBrands,
    archivedBrands: archived,
    brandLabel: (slug) => brandLabelIn(brands, slug),
    // Belt to the server's braces. A creator, writer or brand user must hold no
    // roster even if one is somehow passed — the switcher is not the boundary,
    // canSwitch is.
    switchablePersonas: roster,
    // Normalised as the server's slugForEmail normalises it: the map is keyed on
    // the lowercased address.
    slugFor: (email) => {
      const key = email.trim().toLowerCase();
      return Object.hasOwn(slugByEmail, key) ? slugByEmail[key] : null;
    },
    videoSlugFor: (email) => {
      const key = email.trim().toLowerCase();
      return Object.hasOwn(slugByEmail, key) ? slugByEmail[key]
        : Object.hasOwn(creatorTokens, key) ? creatorTokens[key] : null;
    },
    emailForSlug: (slug) => emailBySlug.get(slug) ?? null,
    videoEmailForSlug: (slug) => emailBySlug.get(slug) ?? emailByCreatorToken.get(slug) ?? null,
    // Narrowed to the LENS, and empty for a lens outside the AM's seat — which
    // is also what keeps a creator's or a brand's browser clean if it were ever
    // handed pods: for anyone who holds no lens the lens IS the identity.
    pods: podsInView(persona, visibleBrands, brandPods, podLeads),
  };

  return <PersonaContext.Provider value={value}>{children}</PersonaContext.Provider>;
}

export function usePersona(): PersonaContextValue {
  const ctx = useContext(PersonaContext);
  if (!ctx) throw new Error("usePersona must be used inside <PersonaProvider>");
  return ctx;
}

/**
 * The context, or null outside a provider: for a small piece drawn on many
 * pages that adds something for one lens and is whole without it (the admin's
 * ⓘ beside a refusal, components/skill-down.tsx), so a test that renders its
 * page without a provider still renders it.
 */
export function usePersonaIfAny(): PersonaContextValue | null {
  return useContext(PersonaContext);
}
